import type { CaptureResult } from "posthog-js";
import { sanitizePostHogProperties } from "./posthog-privacy.ts";
import { endLeavingPageIfMoved, isLeavingPage, isUploadPath } from "./page-leave.ts";

const WEB_VITALS_SAMPLE_RATE = 0.1;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isOpaqueScriptError(event: CaptureResult): boolean {
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions)) {
    const values = event.properties.$exception_values;
    const sources = event.properties.$exception_sources;
    return Array.isArray(values) && values.length === 1 && values[0] === "Script error." &&
      (sources == null || (Array.isArray(sources) && sources.length === 0));
  }
  if (exceptions.length !== 1) return false;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || exception.type !== "Error" || exception.value !== "Script error.") return false;
  const sources = event.properties.$exception_sources;
  if (sources != null && (!Array.isArray(sources) || sources.length > 0)) return false;
  if (exception.stacktrace == null) return true;
  return isRecord(exception.stacktrace) && Array.isArray(exception.stacktrace.frames) &&
    exception.stacktrace.frames.length === 0;
}

function isNativeMediaControlsError(event: CaptureResult): boolean {
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions) || exceptions.length !== 1) return false;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || exception.type !== "TypeError" || exception.value !== "Type error") return false;
  const stacktrace = exception.stacktrace;
  if (!isRecord(stacktrace) || !Array.isArray(stacktrace.frames) || stacktrace.frames.length !== 1) return false;
  const frame: unknown = stacktrace.frames[0];
  if (!isRecord(frame) || frame.function !== "sortedTrackListForMenu") return false;
  return frame.filename == null || frame.filename === "" ||
    frame.filename === "[native code]" || frame.filename === "Unknown Source";
}

const EMPTY_RANGES_VALUE = "Can't find variable: EmptyRanges";
const JAVA_BRIDGE_VALUE = "Error invoking postMessage: Java object is gone";
const IN_APP_BROWSER_FRAME_PREFIX = "iabjs://";
const FIRST_PARTY_ASSET_PATH = "/_next/";
// Exact messages from objects that browsers, wallets, extensions and link
// scanners inject into the page, and the browser's ResizeObserver notice
// (not an error: the notifications are delivered next frame). None of these
// names exist in our code.
const INJECTED_SIGNATURES: ReadonlyArray<{ type: string; value: RegExp }> = [
  { type: "Error", value: /^ResizeObserver loop (?:completed with undelivered notifications\.|limit exceeded)$/ },
  { type: "Error", value: /^'TypeError' captured as exception with message: 'undefined is not an object \(evaluating 'response\.showSearchResults'\)'$/ },
  { type: "Error", value: /^'Error' captured as exception with message: 'No Listener: tabs:outgoing\.message\.ready'$/ },
  { type: "Error", value: /^'TypeError' captured as exception with message: 'undefined is not an object \(evaluating 'n\.standardSelectors'\)'$/ },
  { type: "ReferenceError", value: /^Can't find variable: SCDynimacBridge$/ },
  { type: "TypeError", value: /^undefined is not an object \(evaluating 'window\.__firefox__\.[\w$.]+'\)$/ },
  { type: "ReferenceError", value: /^Can't find variable: __firefox__$/ },
  { type: "TypeError", value: /^undefined is not an object \(evaluating 'window\.ethereum\.selectedAddress = undefined'\)$/ },
  { type: "TypeError", value: /^undefined is not an object \(evaluating 'window\.webkit\.messageHandlers'\)$/ },
  { type: "Error", value: /^'Error' captured as exception with message: 'Invalid call to runtime\.sendMessage\(\)\. Tab not found\.'$/ },
  { type: "UnhandledRejection", value: /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/ },
];

interface SingleException {
  type: string;
  value: string;
  frames: unknown[];
}

/** Exactly one exception with raw SDK frames; null for chains or malformed metadata. */
function getSingleException(event: CaptureResult): SingleException | null {
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions) || exceptions.length !== 1) return null;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || typeof exception.type !== "string" || typeof exception.value !== "string") return null;
  const stacktrace = exception.stacktrace;
  if (stacktrace == null) return { type: exception.type, value: exception.value, frames: [] };
  if (!isRecord(stacktrace) || !Array.isArray(stacktrace.frames)) return null;
  return { type: exception.type, value: exception.value, frames: stacktrace.frames };
}

function getFrameFilename(frame: unknown): string | null | undefined {
  if (!isRecord(frame)) return null;
  if (frame.filename == null) return undefined;
  return typeof frame.filename === "string" ? frame.filename : null;
}

/** Third-party noise that cannot come from our bundles; first-party frames keep it. */
function isThirdPartyNoise(event: CaptureResult): boolean {
  const exception = getSingleException(event);
  if (!exception) return false;
  const { type, value, frames } = exception;
  // WebKit-internal reference with no script location.
  if (type === "ReferenceError" && value === EMPTY_RANGES_VALUE) return frames.length === 0;
  // Meta's Android in-app browser bridge after its Java object is released.
  if (type === "Error" && value === JAVA_BRIDGE_VALUE) {
    return frames.length > 0 && frames.every((frame) =>
      getFrameFilename(frame)?.startsWith(IN_APP_BROWSER_FRAME_PREFIX) === true);
  }
  // Our own upload diagnostics are never noise, whatever their message.
  if (event.properties.tag === "upload-error") return false;
  if (!INJECTED_SIGNATURES.some((signature) => signature.type === type && signature.value.test(value))) return false;
  return frames.every((frame) => {
    const filename = getFrameFilename(frame);
    return filename !== null && !filename?.includes(FIRST_PARTY_ASSET_PATH);
  });
}

// Browser messages for a fetch the browser gave up on (WebKit, Chromium, Firefox).
const NETWORK_ERROR_VALUES = new Set(["Load failed", "Failed to fetch", "NetworkError when attempting to fetch resource."]);

/** Upload diagnostics, or an event from an upload page: never dropped as navigation noise. */
function hasUploadContext(event: CaptureResult): boolean {
  const properties = event.properties;
  if (properties.tag === "upload-error") return true;
  for (const key of ["$current_url", "$pathname"] as const) {
    const value: unknown = properties[key];
    if (value == null) continue;
    if (typeof value !== "string") return true;
    let pathname: string;
    try {
      pathname = key === "$current_url" ? new URL(value).pathname : value;
    } catch {
      return true;
    }
    if (isUploadPath(pathname)) return true;
  }
  return false;
}

/**
 * A request cancelled because our own code just started a full page load away
 * (markLeavingPage), e.g. the rest of the login code action's response. Only
 * stackless TypeErrors, only while the marked page is still showing, never
 * on upload pages; network errors while the page stays are kept.
 */
function isCancelledByOurNavigation(event: CaptureResult): boolean {
  const exception = getSingleException(event);
  if (!exception || exception.type !== "TypeError" || !NETWORK_ERROR_VALUES.has(exception.value)) return false;
  return exception.frames.length === 0 && !hasUploadContext(event) && isLeavingPage();
}

function isFilteredException(event: CaptureResult): boolean {
  try {
    return isOpaqueScriptError(event) || isNativeMediaControlsError(event) || isThirdPartyNoise(event) ||
      isCancelledByOurNavigation(event);
  } catch {
    // Unreadable exception metadata is kept rather than thrown into capture().
    return false;
  }
}

const INVALID_STATE_VALUE = "InvalidStateError: The object is in an invalid state.";

/**
 * WebKit gives a DOMException it creates itself no stack, so the stackless
 * InvalidStateErrors on iPhone upload pages can't be traced to their source.
 * This snapshot of what the page was doing when it was captured tells the
 * candidates apart (a photo morph, the lightbox, the photo wall's
 * animations, video, the share dialog, a hidden page). Only booleans, counts,
 * the visibility state and the page age: no URL, id or text. Null when it
 * doesn't apply or the page can't be read.
 */
function getInvalidStateContext(event: CaptureResult): Record<string, string | number | boolean> | null {
  try {
    const exception = getSingleException(event);
    if (!exception || exception.frames.length > 0) return null;
    if (exception.type !== "DOMException" && exception.type !== "InvalidStateError") return null;
    if (exception.value !== INVALID_STATE_VALUE && exception.value !== "The object is in an invalid state.") return null;
    const pathname = getEventPathname(event);
    if (!pathname || !isUploadPath(pathname) || typeof document === "undefined" || !document) return null;
    const root = document.documentElement;
    const videos = Array.from(document.querySelectorAll("video"));
    const fullscreen = document.fullscreenElement ??
      (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement;
    return {
      invalid_state_visibility: String(document.visibilityState).slice(0, 20),
      invalid_state_photo_morph: Boolean(root?.dataset.galleryMorph),
      invalid_state_lightbox_open: document.querySelector(".yarl__portal_open") !== null,
      invalid_state_dialog_open: document.querySelector('[role="alertdialog"]') !== null,
      invalid_state_photowall_flights: document.querySelectorAll("[data-photowall-flight]").length,
      invalid_state_animations: typeof document.getAnimations === "function" ? document.getAnimations().length : -1,
      invalid_state_videos: videos.length,
      invalid_state_videos_playing: videos.filter((video) => !video.paused).length,
      invalid_state_fullscreen: Boolean(fullscreen),
      invalid_state_page_ms: typeof performance === "undefined" ? -1 : Math.round(performance.now()),
    };
  } catch {
    // The snapshot is optional: the error is sent without it.
    return null;
  }
}

function shouldKeepWebVitals(sessionId: unknown): boolean {
  if (typeof sessionId !== "string" || !sessionId) return true;
  let hash = 2166136261;
  for (let index = 0; index < sessionId.length; index++) {
    hash = Math.imul(hash ^ sessionId.charCodeAt(index), 16777619);
  }
  return (hash >>> 0) % 100 < WEB_VITALS_SAMPLE_RATE * 100;
}

export function beforeSendToPostHog(event: CaptureResult | null): CaptureResult | null {
  try {
    return prepareForPostHog(event);
  } catch {
    // posthog-js lets a before_send error escape capture() into the caller.
    // Drop this one event instead of throwing or sending it unsanitized.
    return null;
  }
}

/** The path of the page an event belongs to, when it says. */
function getEventPathname(event: CaptureResult): string | undefined {
  try {
    const { $pathname: pathname, $current_url: currentUrl } = event.properties;
    if (typeof pathname === "string") return pathname;
    return typeof currentUrl === "string" ? new URL(currentUrl).pathname : undefined;
  } catch {
    return undefined;
  }
}

function prepareForPostHog(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  // Every event, not only exceptions: the leave mark ends for good as soon as
  // another page shows (markLeavingPage).
  endLeavingPageIfMoved(getEventPathname(event));
  // Autocapture is on only so posthog-js can emit $rageclick; ordinary
  // clicks and copied text are not sent.
  if (event.event === "$autocapture" || event.event === "$copy_autocapture") return null;
  if (event.event === "$exception" && isFilteredException(event)) return null;
  if (event.event === "$web_vitals" && !shouldKeepWebVitals(event.properties.$session_id)) return null;
  const invalidStateContext = event.event === "$exception" ? getInvalidStateContext(event) : null;
  const properties = sanitizePostHogProperties(event.properties, true);
  const environment = process.env.NEXT_PUBLIC_VERCEL_ENV;
  const hasMetadata = Boolean(environment);
  const result: CaptureResult = {
    ...event,
    properties: {
      ...properties,
      ...invalidStateContext,
      ...(hasMetadata ? {
        platform: "web",
        deployment_environment: environment,
        git_commit: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
        telemetry_schema_version: 2,
      } : {}),
      ...(event.event === "$web_vitals" ? {
        web_vitals_sample_rate: typeof event.properties.$session_id === "string" && event.properties.$session_id
          ? WEB_VITALS_SAMPLE_RATE : 1,
      } : {}),
    },
  };
  for (const key of ["$set", "$set_once"] as const) {
    const value = event[key];
    if (isRecord(value)) result[key] = sanitizePostHogProperties(value);
  }
  const hasTopLevelChanges = result.$set !== event.$set || result.$set_once !== event.$set_once;
  return properties === event.properties && !hasMetadata && !invalidStateContext &&
    event.event !== "$web_vitals" && !hasTopLevelChanges ? event : result;
}
